import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, { useEffect, useRef } from 'react';
import { $generateNodesFromDOM } from '@lexical/html';
import { $getRoot } from 'lexical';
import { sanitizeHTML } from '../utils/sanitize';
import { logger } from '../utils/logger';
import LexicalToolbar from './LexicalToolbar';
import './LexicalEditor.css';

const theme = {
  heading: {
    h1: 'lexical-h1',
    h2: 'lexical-h2',
    h3: 'lexical-h3',
  },
  text: {
    bold: 'lexical-bold',
    italic: 'lexical-italic',
    underline: 'lexical-underline',
    strikethrough: 'lexical-strikethrough',
    code: 'lexical-code',
  },
  list: {
    nested: {
      listitem: 'lexical-nested-listitem',
    },
    ol: 'lexical-list-ol',
    ul: 'lexical-list-ul',
    listitem: 'lexical-listitem',
  },
  quote: 'lexical-quote',
  link: 'lexical-link',
};

// When the editor changes, get the JSON state
function OnChangePlugin({ onChange }) {
  const [editor] = useLexicalComposerContext();
  const isInitialMount = useRef(true);
  const debounceTimer = useRef(null);
  
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      // Skip the first update (initial load)
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      
      // Debounce onChange calls to reduce state updates during typing
      debounceTimer.current = setTimeout(() => {
        editorState.read(() => {
          const json = editorState.toJSON();
          if (onChange) {
            onChange(JSON.stringify(json));
          }
        });
      }, 300); // 300ms debounce delay
    });
  }, [editor, onChange]);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);
  
  return null;
}

// Load initial content
function InitialContentPlugin({ initialContent, isInitialized }) {
  const [editor] = useLexicalComposerContext();
  const hasLoaded = useRef(false);
  
  useEffect(() => {
    if (hasLoaded.current || !isInitialized) return;
    
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      
      if (!initialContent) {
        hasLoaded.current = true;
        return;
      }
      
      try {
        // Try to parse as JSON (Lexical editor state)
        const parsed = typeof initialContent === 'string' ? JSON.parse(initialContent) : initialContent;
        
        if (parsed && parsed.root) {
          // It's a Lexical editor state JSON - load it directly
          try {
            const editorState = editor.parseEditorState(JSON.stringify(parsed));
            editor.setEditorState(editorState);
            hasLoaded.current = true;
            return;
          } catch (parseError) {
            // If parseEditorState fails, try loading as HTML
            logger.warn('Failed to parse Lexical state, trying HTML:', parseError);
          }
        }
      } catch (e) {
        // Not JSON, continue to try HTML/text
      }
      
      // Try as HTML string - sanitize first to prevent XSS
      try {
        const sanitizedContent = sanitizeHTML(initialContent);
        const parser = new DOMParser();
        const dom = parser.parseFromString(sanitizedContent, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        root.append(...nodes);
        hasLoaded.current = true;
      } catch (e2) {
        // Fallback: treat as plain text (sanitized)
        const sanitizedText = sanitizeHTML(initialContent);
        const textNode = root.createTextNode(sanitizedText);
        root.append(textNode);
        hasLoaded.current = true;
      }
    });
  }, [editor, initialContent, isInitialized]);
  
  return null;
}

function LexicalEditor({ value, onChange, placeholder, className, error }) {
  const [isInitialized, setIsInitialized] = React.useState(false);
  
  const initialConfig = {
    namespace: 'FeatureEditor',
    theme,
    onError: (error) => {
      logger.error('Lexical error:', error);
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      LinkNode,
    ],
    editorState: null, // We'll load this via the plugin
  };

  React.useEffect(() => {
    setIsInitialized(true);
  }, []);

  return (
    <div className={`lexical-editor-wrapper ${className || ''} ${error ? 'error' : ''}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <LexicalToolbar />
        <div className="lexical-editor-container">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="lexical-content-editable" />
            }
            placeholder={
              <div className="lexical-placeholder">{placeholder || 'Enter description...'}</div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <InitialContentPlugin initialContent={value} isInitialized={isInitialized} />
          <OnChangePlugin onChange={onChange} />
        </div>
      </LexicalComposer>
    </div>
  );
}

export default LexicalEditor;
